import type { ReactNode } from "react";

type Tone = "neutral" | "info" | "success" | "warning" | "danger" | "brand";

interface BadgeProps {
  tone?: Tone;
  children: ReactNode;
  className?: string;
}

/** Generic tonal pill badge. For project status use the existing StatusBadge. */
export function Badge({ tone = "neutral", children, className }: BadgeProps) {
  return <span className={`badge badge--${tone} ${className ?? ""}`.trim()}>{children}</span>;
}

type ChipTone = "ok" | "warn" | "err" | "info" | "planned";

const CHIP_LABEL: Record<string, { tone: ChipTone; label: string }> = {
  qa_ready: { tone: "warn", label: "QA-Ready" },
  draft: { tone: "info", label: "Entwurf" },
  planned: { tone: "planned", label: "Geplant" },
  disabled: { tone: "planned", label: "Deaktiviert" },
  active: { tone: "ok", label: "Aktiv" },
  ok: { tone: "ok", label: "OK" },
  error: { tone: "err", label: "Fehler" },
};

interface StatusChipProps {
  /** Known status key (qa_ready | draft | planned | disabled | active | ok | error) or custom. */
  status?: string;
  tone?: ChipTone;
  children?: ReactNode;
}

/** Dot + label status chip. Resolves known status keys to a tone + German label. */
export function StatusChip({ status, tone, children }: StatusChipProps) {
  const known = status ? CHIP_LABEL[status] : undefined;
  const resolvedTone: ChipTone = tone ?? known?.tone ?? "planned";
  const label = children ?? known?.label ?? status ?? "";
  return (
    <span className={`chip chip--${resolvedTone}`}>
      <span className="chip__dot" />
      {label}
    </span>
  );
}
